import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CASE_STATUSES, statusMeta } from "@/lib/constants";
import { Users, CalendarClock, Activity, MapPin, Loader2 } from "lucide-react";

export default function Dashboard() {
  const [m, setM] = useState(null);

  useEffect(() => {
    api.get("/metrics").then(({ data }) => setM(data)).catch(() => setM(false));
  }, []);

  if (m === null) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-orange-600" /></div>;
  if (m === false) return <p className="text-red-600">No se pudieron cargar las métricas.</p>;

  const miembroLabels = { "Miembro inferior": "Inferior", "Miembro superior": "Superior", "Ambos": "Ambos" };

  return (
    <div>
      <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">Panel de resumen</h1>
      <p className="text-slate-500 mt-1">Vista general del programa</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <StatCard icon={Users} label="Pacientes registrados" value={m.total} color="text-orange-600" bg="bg-orange-50" testId="stat-total" />
        <StatCard icon={CalendarClock} label="Últimos 7 días" value={m.last7} color="text-emerald-600" bg="bg-emerald-50" testId="stat-week" />
        <StatCard icon={Activity} label="En preevaluación" value={m.by_status?.preevaluacion || 0} color="text-indigo-600" bg="bg-indigo-50" testId="stat-preeval" />
        <StatCard icon={MapPin} label="Prótesis entregadas" value={m.by_status?.entregada || 0} color="text-green-600" bg="bg-green-50" testId="stat-entregadas" />
      </div>

      <div className="grid lg:grid-cols-3 gap-5 mt-6">
        <Panel title="Por miembro afectado">
          {Object.keys(m.by_miembro || {}).length === 0 && <Empty />}
          {Object.entries(m.by_miembro || {}).map(([k, v]) => (
            <BarRow key={k} label={miembroLabels[k] || k} value={v} total={m.total} color="bg-orange-500" />
          ))}
        </Panel>

        <Panel title="Por estado del caso">
          {CASE_STATUSES.map((s) => (
            <div key={s.value} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
              <span className="flex items-center gap-2 text-sm text-slate-700">
                <span className={`h-2.5 w-2.5 rounded-full ${s.dot}`} /> {s.label}
              </span>
              <span className="font-semibold text-slate-900">{m.by_status?.[s.value] || 0}</span>
            </div>
          ))}
        </Panel>

        <Panel title="Top 5 estados (región)">
          {Object.keys(m.top_estados || {}).length === 0 && <Empty />}
          {Object.entries(m.top_estados || {}).map(([k, v]) => (
            <BarRow key={k} label={k} value={v} total={m.total} color="bg-emerald-500" />
          ))}
        </Panel>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, bg, testId }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid={testId}>
      <div className={`h-10 w-10 rounded-lg ${bg} flex items-center justify-center mb-3`}>
        <Icon className={`h-5 w-5 ${color}`} />
      </div>
      <p className="font-heading text-3xl font-bold text-slate-900">{value}</p>
      <p className="text-sm text-slate-500 mt-1">{label}</p>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <h3 className="font-heading font-semibold text-slate-900 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function BarRow({ label, value, total, color }) {
  const pct = total ? Math.round((value / total) * 100) : 0;
  return (
    <div className="py-2">
      <div className="flex justify-between text-sm mb-1">
        <span className="text-slate-700">{label}</span>
        <span className="font-semibold text-slate-900">{value}</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Empty() {
  return <p className="text-sm text-slate-400 py-2">Sin datos aún.</p>;
}
